import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Play, Square, Clock } from 'lucide-react'
import { toast } from 'sonner'
import { differenceInSeconds } from 'date-fns'
import { timeTrackingApi } from '@/lib/api'
import { useAgencyTheme } from '@/contexts/AgencyThemeContext'
import { getTimezoneAbbreviation } from '@/utils/timezone'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { TimeEntry, Client, StartTimerRequest, StopTimerRequest } from '@/types'

interface TimerWidgetProps {
  activeTimer: TimeEntry | null | undefined
  clients: Client[]
}

export default function TimerWidget({ activeTimer, clients }: TimerWidgetProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { timezone } = useAgencyTheme()
  const [isStartDialogOpen, setIsStartDialogOpen] = useState(false)
  const [isStopDialogOpen, setIsStopDialogOpen] = useState(false)
  const [description, setDescription] = useState('')
  const [clientId, setClientId] = useState<string>('')
  const [elapsedTime, setElapsedTime] = useState(0)

  // Update elapsed time every second when timer is running
  useEffect(() => {
    if (!activeTimer) {
      setElapsedTime(0)
      return
    }

    const updateElapsed = () => {
      const start = new Date(activeTimer.start_time)
      const now = new Date()
      const seconds = differenceInSeconds(now, start)
      setElapsedTime(Math.max(0, seconds))
    }

    updateElapsed()
    const interval = setInterval(updateElapsed, 1000)
    return () => clearInterval(interval)
  }, [activeTimer])

  // Start timer mutation
  const startMutation = useMutation({
    mutationFn: (data: StartTimerRequest) => timeTrackingApi.startTimer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activeTimer'] })
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] })
      toast.success(t('timeTracking.timerStarted'))
      setIsStartDialogOpen(false)
      setDescription('')
      setClientId('')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || t('timeTracking.startError'))
    },
  })

  // Stop timer mutation
  const stopMutation = useMutation({
    mutationFn: (data?: StopTimerRequest) => timeTrackingApi.stopTimer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activeTimer'] })
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] })
      toast.success(t('timeTracking.timerStopped'))
      setIsStopDialogOpen(false)
      setDescription('')
      setClientId('')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('timeTracking.stopError')
      toast.error(errorMessage)
    },
  })

  // Format elapsed time
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const handleStartTimer = (e: React.FormEvent) => {
    e.preventDefault()
    if (!description.trim()) return
    startMutation.mutate({
      description: description.trim(),
      client_id: clientId || undefined,
    })
  }

  const handleStopTimer = (e: React.FormEvent) => {
    e.preventDefault()
    stopMutation.mutate({
      description: description.trim() || undefined,
      client_id: clientId || undefined,
    })
  }

  const openStopDialog = () => {
    if (activeTimer) {
      setDescription(activeTimer.description)
      setClientId(activeTimer.client_id || '')
    }
    setIsStopDialogOpen(true)
  }

  return (
    <>
      {/* Timer Display */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-3">
          {activeTimer ? (
            <>
              <div className="flex items-center gap-2 bg-red-100 dark:bg-red-900/30 px-4 py-2 rounded-lg">
                <Clock className="h-5 w-5 text-red-600 dark:text-red-400 animate-pulse" />
                <span className="font-mono text-lg font-bold text-red-600 dark:text-red-400">
                  {formatTime(elapsedTime)}
                </span>
                <span className="text-sm text-red-600/80 dark:text-red-400/80 max-w-[150px] truncate">
                  {activeTimer.description}
                </span>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={openStopDialog}
                disabled={stopMutation.isPending}
              >
                <Square className="h-4 w-4 mr-2" />
                {t('timeTracking.stop')}
              </Button>
            </>
          ) : (
            <Button onClick={() => setIsStartDialogOpen(true)} disabled={startMutation.isPending}>
              <Play className="h-4 w-4 mr-2" />
              {t('timeTracking.startTimer')}
            </Button>
          )}
        </div>
        {/* Timezone indicator */}
        <div className="text-xs text-muted-foreground ml-1">
          Timezone: {getTimezoneAbbreviation(timezone)}
        </div>
      </div>

      {/* Start Timer Dialog */}
      <Dialog open={isStartDialogOpen} onOpenChange={setIsStartDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <form onSubmit={handleStartTimer}>
            <DialogHeader>
              <DialogTitle>{t('timeTracking.startTimer')}</DialogTitle>
              <DialogDescription>{t('timeTracking.startTimerDescription')}</DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="timer-description">{t('timeTracking.whatAreYouWorkingOn')} *</Label>
                <Input
                  id="timer-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('timeTracking.descriptionPlaceholder')}
                  required
                  autoFocus
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="timer-client">{t('timeTracking.client')}</Label>
                <Select value={clientId || 'none'} onValueChange={(val) => setClientId(val === 'none' ? '' : val)}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('timeTracking.selectClient')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('timeTracking.noClient')}</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.client_id} value={client.client_id}>
                        {client.client_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsStartDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={startMutation.isPending}>
                <Play className="h-4 w-4 mr-2" />
                {t('timeTracking.start')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stop Timer Dialog */}
      <Dialog open={isStopDialogOpen} onOpenChange={setIsStopDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <form onSubmit={handleStopTimer}>
            <DialogHeader>
              <DialogTitle>{t('timeTracking.stopTimer')}</DialogTitle>
              <DialogDescription>{t('timeTracking.stopTimerDescription')}</DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="text-center py-4">
                <div className="text-3xl font-mono font-bold text-primary">
                  {formatTime(elapsedTime)}
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="stop-description">{t('timeTracking.description')}</Label>
                <Input
                  id="stop-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('timeTracking.descriptionPlaceholder')}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="stop-client">{t('timeTracking.client')}</Label>
                <Select value={clientId || 'none'} onValueChange={(val) => setClientId(val === 'none' ? '' : val)}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('timeTracking.selectClient')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('timeTracking.noClient')}</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.client_id} value={client.client_id}>
                        {client.client_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsStopDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="destructive" disabled={stopMutation.isPending}>
                <Square className="h-4 w-4 mr-2" />
                {t('timeTracking.stop')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}
